import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useContext, useState } from "react";
import { useSetSftpOutboundReportEnabledMutation } from "store/queries/sftpPartners";
import {
  ISftpOutboundReport,
  ISftpOutboundReportFile,
  ISftpPartnerDetail,
} from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import {
  formatBytes,
  outboundPeriodLabel,
  outboundReportTypeLabel,
  outboundSentByLabel,
  relativeTime,
  sftpApiErrorMessage,
} from "../../utils/sftpPartners";
import { SftpAccountName } from "../SftpPartnerParts";
import { SftpStatusTag } from "../SftpPartnersTab/SftpPartnersTab";
import { OutboundReportDialog } from "./OutboundReportDialog";
import { SendNowDialog } from "./SendNowDialog";

type DialogState =
  | { kind: "create" }
  | { kind: "edit"; report: ISftpOutboundReport }
  | { kind: "send"; report: ISftpOutboundReport }
  | null;

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div className="col-12 md:col-6">
      <div className="text-600 text-sm mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

function LastFile({ file }: { file: ISftpOutboundReportFile | null }) {
  if (!file) return <span className="text-600">No file sent yet.</span>;
  const when = (
    <span title={formatDateTime(file.createdAt)}>
      {relativeTime(file.createdAt)}
    </span>
  );
  if (file.status === "FAILED") {
    return (
      <span className="text-red-600" data-testid="sftp-outbound-last-failed">
        Failed: {file.error || "unknown error"} · {file.fileName} ·{" "}
        {outboundPeriodLabel(file.periodStart, file.periodEnd)} · {when}
      </span>
    );
  }
  return (
    <span data-testid="sftp-outbound-last-file">
      <code>{file.fileName}</code> ·{" "}
      {outboundPeriodLabel(file.periodStart, file.periodEnd)} ·{" "}
      {plural(file.rowCount, "row", "rows")} ({formatBytes(file.byteSize)}) ·{" "}
      {when}
    </span>
  );
}

const fileCreatedBody = (f: ISftpOutboundReportFile) =>
  formatDateTime(f.createdAt);

const fileNameBody = (f: ISftpOutboundReportFile) => (
  <code title={`${f.s3Key} · ${formatBytes(f.byteSize)}`}>{f.fileName}</code>
);

const filePeriodBody = (f: ISftpOutboundReportFile) =>
  outboundPeriodLabel(f.periodStart, f.periodEnd);

const fileStatusBody = (f: ISftpOutboundReportFile) =>
  f.status === "FAILED" ? (
    <Tag severity="danger" value="Failed" title={f.error ?? undefined} />
  ) : (
    <Tag severity="success" value="Succeeded" />
  );

const fileSentByBody = (f: ISftpOutboundReportFile) =>
  outboundSentByLabel(f.triggeredByUserId);

function OutboundReportBlock({
  partner,
  report,
  onEdit,
  onSend,
}: {
  partner: ISftpPartnerDetail;
  report: ISftpOutboundReport;
  onEdit: () => void;
  onSend: () => void;
}) {
  const toastRef = useContext(ToastContext);
  const [setEnabled, { isLoading }] = useSetSftpOutboundReportEnabledMutation();
  const nextEnabled = !report.enabled;
  const label = outboundReportTypeLabel(report.reportType);
  const folderPath = `${partner.s3Prefix}${report.folder}`;

  const onToggle = () => {
    confirmDialog({
      header: `${nextEnabled ? "Enable" : "Disable"} the ${label}?`,
      icon: "pi pi-exclamation-triangle",
      message: nextEnabled
        ? `Every morning at 05:00 Central the daily job writes the previous day's (UTC) file into ${folderPath}.`
        : "The daily job stops writing this file. Files already sent stay in the folder, and Send now still works.",
      acceptLabel: nextEnabled ? "Enable" : "Disable",
      rejectLabel: "Cancel",
      acceptClassName: nextEnabled ? undefined : "p-button-danger",
      accept: async () => {
        try {
          await setEnabled({
            slug: partner.slug,
            id: report.id,
            enabled: nextEnabled,
          }).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: nextEnabled
              ? "Outgoing report enabled"
              : "Outgoing report disabled",
            detail: `The ${label} is now ${
              nextEnabled ? "enabled" : "disabled"
            }.`,
            life: 5000,
          });
        } catch (error) {
          toastRef?.current?.show({
            severity: "error",
            summary: nextEnabled
              ? "Couldn't enable the outgoing report"
              : "Couldn't disable the outgoing report",
            detail: sftpApiErrorMessage(error, "Please try again."),
            life: 8000,
          });
        }
      },
    });
  };

  return (
    <div
      className="p-3 border-1 surface-border border-round"
      data-testid="sftp-outbound-report"
    >
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <div className="flex flex-wrap align-items-center gap-2">
          <span className="font-semibold">{label}</span>
          <SftpStatusTag enabled={report.enabled} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            label="Edit"
            icon="pi pi-pencil"
            className="p-button-outlined p-button-sm"
            onClick={onEdit}
            data-testid="sftp-outbound-edit"
          />
          <Button
            label={report.enabled ? "Disable" : "Enable"}
            icon={report.enabled ? "pi pi-pause" : "pi pi-play"}
            className={
              report.enabled
                ? "p-button-danger p-button-outlined p-button-sm"
                : "p-button-sm"
            }
            onClick={onToggle}
            loading={isLoading}
            data-testid="sftp-outbound-toggle"
          />
          <Button
            label="Send now"
            icon="pi pi-send"
            className="p-button-outlined p-button-sm"
            onClick={onSend}
            data-testid="sftp-outbound-send"
          />
        </div>
      </div>
      <div className="grid">
        <Field label="Account">
          <SftpAccountName
            accountId={report.accountId}
            accountName={report.accountName}
          />
        </Field>
        <Field label="Folder">
          <code>{folderPath}</code>
        </Field>
        <Field label="File name">
          <code>{`${report.fileNamePrefix}_YYYYMMDD.csv`}</code>
        </Field>
        <Field label="Last file">
          <LastFile file={report.lastFile} />
        </Field>
      </div>
      <div className="font-semibold mt-2 mb-2">Recent files</div>
      <DataTable
        value={report.recentFiles}
        dataKey="id"
        emptyMessage="No files yet."
        responsiveLayout="scroll"
        data-testid="sftp-outbound-recent-files"
      >
        <Column header="Created" body={fileCreatedBody} />
        <Column header="File" body={fileNameBody} />
        <Column header="Period" body={filePeriodBody} />
        <Column header="Rows" field="rowCount" />
        <Column header="Status" body={fileStatusBody} />
        <Column header="Sent by" body={fileSentByBody} />
      </DataTable>
    </div>
  );
}

// Files we write into the partner's folder on a schedule (CLE-2864). Shown for every kind of partner.
export function OutboundReportsCard({
  partner,
}: {
  partner: ISftpPartnerDetail;
}) {
  const [dialog, setDialog] = useState<DialogState>(null);
  const reports = partner.outboundReports ?? [];
  const close = () => setDialog(null);

  return (
    <Card title="Outgoing reports">
      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <span className="text-600 text-sm">
          Files we write into this partner&apos;s folder every morning (05:00
          Central, covering the previous UTC day) while enabled.
        </span>
        <Button
          label="Add outgoing report"
          icon="pi pi-plus"
          className="p-button-outlined"
          onClick={() => setDialog({ kind: "create" })}
          data-testid="sftp-outbound-add"
        />
      </div>
      {reports.length ? (
        <div className="flex flex-column gap-3">
          {reports.map((report) => (
            <OutboundReportBlock
              key={report.id}
              partner={partner}
              report={report}
              onEdit={() => setDialog({ kind: "edit", report })}
              onSend={() => setDialog({ kind: "send", report })}
            />
          ))}
        </div>
      ) : (
        <span>No outgoing reports.</span>
      )}
      {dialog?.kind === "create" ? (
        <OutboundReportDialog partner={partner} onHide={close} />
      ) : null}
      {dialog?.kind === "edit" ? (
        <OutboundReportDialog
          partner={partner}
          report={dialog.report}
          onHide={close}
        />
      ) : null}
      {dialog?.kind === "send" ? (
        <SendNowDialog
          partner={partner}
          report={dialog.report}
          onHide={close}
        />
      ) : null}
    </Card>
  );
}
